import { mapGetters, mapActions } from 'vuex'
import { required, email } from 'vuelidate/lib/validators'
import * as components from 'src/components'
import { oneOf } from 'src/utils'
import {
  oauthGoogleAuthenticator,
  oauthFacebookAuthenticator,
  oauthAppleAuthenticator
} from 'src/authenticator'
import { Constants } from 'src/constants'
import recaptchaService from 'src/recaptcha'
import roistat from 'src/roistat'

export default {
  components: {
    ...components
  },
  computed: {
    ...mapGetters({
      config: 'config/config',
      context: 'passport/context',
      user: 'passport/user'
    }),
    oauthTelegram () {
      return this.config.oauth == null
        ? null
        : this.config.oauth.telegram
    },
    forgotLink () {
      return {
        path: '/security/forgot',
        query: this.$route.query
      }
    },
    signinLink () {
      return {
        path: '/security/signin',
        query: this.$route.query
      }
    },
    hasOauthGoogleURL () {
      return this.config.oauth != null && this.config.oauth.google != null
    },
    hasOauthFacebookURL () {
      return this.config.oauth != null && this.config.oauth.facebook != null
    },
    hasOauthAppleURL () {
      return this.config.oauth != null && this.config.oauth.apple != null
    }
  },
  data () {
    return {
      form: {
        login: '',
        password: ''
      },
      confirmationForm: {
        code: ''
      },
      error: null,
      isErrorVisible: false,
      isBusy: false,
      isPasswordVisible: false,
      isResendCode: false,
      emailConfirmationCodeLength: Constants.confirmation.emailConfirmationCodeLength,
      colorTheme: Constants.colorTheme
    }
  },
  mounted () {
    const { login } = this.$route.query
    if (login != null) {
      this.form.login = login
    }
  },
  validations: {
    form: {
      login: {
        required,
        email
      },
      password: {
        required,
        isComplex () {
          return this.form.password.length >= 8
        }
      }
    },
    confirmationForm: {
      code: {
        required,
        isComplex () {
          return this.confirmationForm.code.length === this.emailConfirmationCodeLength
        }
      }
    }
  },
  watch: {
    'confirmationForm.code': {
      async handler () {
        if (!this.isResendCode &&
            this.confirmationForm.code !== null &&
            this.confirmationForm.code.length === this.emailConfirmationCodeLength) {
          await this.handleConfirm()
        }
      }
    }
  },
  methods: {
    ...mapActions({
      signup: 'passport/signup',
      signout: 'passport/signout',
      confirmEmailByCode: 'passport/confirmEmailByCode',
      describeError: 'errors/describeError'
    }),
    oneOf,
    openLink (link) {
      this.$router.push(link)
    },
    async socialClick (socialName) {
      if (socialName === 'google') {
        await this.oauthGoogle()
      } else if (socialName === 'facebook') {
        this.oauthFacebook()
      } else if (socialName === 'apple') {
        this.oauthApple()
      }
    },
    async oauthGoogle () {
      const roistatVisitId = roistat.getVisit()
      await oauthGoogleAuthenticator(this.config, {
        scope: this.context.scope,
        state: this.context.state,
        refcode: this.context.refcode,
        roistatVisitId: roistatVisitId
      })
    },
    async oauthFacebook () {
      const roistatVisitId = roistat.getVisit()
      await oauthFacebookAuthenticator(this.config, {
        scope: this.context.scope,
        state: this.context.state,
        refcode: this.context.refcode,
        roistatVisitId: roistatVisitId
      })
    },
    async oauthApple () {
      const roistatVisitId = roistat.getVisit()
      await oauthAppleAuthenticator(this.config, {
        scope: this.context.scope,
        state: this.context.state,
        refcode: this.context.refcode,
        roistatVisitId: roistatVisitId
      })
    },
    handleLogin (value) {
      this.$v.$reset()
      this.form.login = value
    },
    handlePassword (value) {
      this.$v.$reset()
      this.form.password = value
    },
    async handleCode (value) {
      this.confirmationForm.code = value
    },
    async confirmCode () {
      this.$v.confirmationForm.$touch()
      if (!this.$v.confirmationForm.$invalid) {
        this.handleConfirm()
      }
    },
    async handleBack () {
      if (this.user) {
        this.confirmationForm.code = null
        this.isResendCode = false
        await this.signout()
      } else {
        this.$router.push({
          path: '/security/signin',
          query: this.$route.query
        })
      }
    },
    togglePasswordVisibility () {
      this.isPasswordVisible = !this.isPasswordVisible
    },
    async handleSignUp () {
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        await this.handleSubmit()
      }
    },
    async handleSubmit () {
      this.isBusy = true
      this.isErrorVisible = false
      this.error = null
      setImmediate(async () => {
        try {
          const recaptchaToken = await recaptchaService.getToken()
          const roistatVisitId = roistat.getVisit()

          const { signUp } = await this.signup({
            login: this.form.login,
            password: this.form.password,
            recaptchaToken,
            roistatVisitId
          })
          if (!signUp) {
            this.$router.push({
              path: '/security/grant',
              query: this.$route.query
            })
          }
        } catch (e) {
          const { errorMessage } = await this.describeError({ error: e })
          this.error = errorMessage
          this.isErrorVisible = true
        } finally {
          this.isBusy = false
        }
      })
    },
    async handleConfirm () {
      if (this.isBusy) {
        return
      }

      this.isBusy = true
      this.isErrorVisible = false
      this.error = null
      setImmediate(async () => {
        try {
          await this.confirmEmailByCode({
            code: this.confirmationForm.code
          })
          setImmediate(() => {
            this.$router.push({
              path: '/security/grant',
              query: this.$route.query
            })
          })
        } catch (e) {
          const { errorMessage } = await this.describeError({ error: e })
          this.error = errorMessage
          this.isErrorVisible = true
        } finally {
          this.isBusy = false
          this.isResendCode = true
        }
      })
    },
    async handleReset () {
      this.signout()
      this.$router.push({
        path: '/security/signin',
        query: this.$route.query
      })
    }
  }
}
